(() => {
  const SELECTOR = '[data-legacy-hero-spray]';

  class LegacyHeroSpray {
    constructor(section) {
      this.section = section;
      this.bottle = section.querySelector('.lv5-bottle');
      this.cap = section.querySelector('.lv5-bottle__cap');
      this.atomizer = section.querySelector('.lv5-bottle__atomizer i');
      this.canvas = section.querySelector('.lv5-hero__spray-canvas');
      this.context = this.canvas?.getContext('2d');
      this.interval = Math.max(2000, Number(section.dataset.sprayInterval) || 3000);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isVisible = true;
      this.timer = null;
      this.resetTimer = null;
      this.emitTimer = null;
      this.animationFrame = null;
      this.particles = [];

      if (!this.bottle || !this.cap) return;

      this.onCapClick = () => this.spray(true);
      this.onVisibilityChange = () => this.syncTimer();
      this.onMotionChange = () => this.syncTimer();
      this.onResize = () => this.resizeCanvas();
      this.cap.addEventListener('click', this.onCapClick);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion.addEventListener?.('change', this.onMotionChange);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.resizeCanvas();

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          this.syncTimer();
        }, { threshold: 0.3 });
        this.observer.observe(section);
      }

      this.syncTimer();
    }

    spray(manual = false) {
      if (!this.bottle || (!manual && (this.reducedMotion.matches || !this.isVisible || document.hidden))) return;

      window.clearTimeout(this.resetTimer);
      this.bottle.classList.remove('is-spraying');
      this.section.classList.remove('is-spraying');
      void this.bottle.offsetWidth;
      this.bottle.classList.add('is-spraying');
      this.section.classList.add('is-spraying');
      window.clearTimeout(this.emitTimer);
      this.emitTimer = window.setTimeout(() => this.emitLiquidMist(), 350);
      this.resetTimer = window.setTimeout(() => {
        this.bottle.classList.remove('is-spraying');
        this.section.classList.remove('is-spraying');
      }, 2150);
      if (manual) this.syncTimer();
    }

    resizeCanvas() {
      if (!this.canvas || !this.context) return;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.canvas.width = Math.round(window.innerWidth * ratio);
      this.canvas.height = Math.round(window.innerHeight * ratio);
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    emitLiquidMist() {
      if (!this.context || !this.atomizer || this.reducedMotion.matches) return;

      const nozzle = this.atomizer.getBoundingClientRect();
      const originX = nozzle.right;
      const originY = nozzle.top + nozzle.height * 0.48;
      const isMobile = window.innerWidth < 750;
      const count = isMobile ? 135 : 320;
      const now = performance.now();

      this.particles = Array.from({ length: count }, (_, index) => {
        const angle = Math.random() * Math.PI * 2;
        const reach = isMobile
          ? 0.35 + Math.pow(Math.random(), 0.62) * 0.8
          : 0.3 + Math.pow(Math.random(), 0.68) * 0.64;
        const horizontalBias = isMobile ? 0 : window.innerWidth * 0.07;
        const spreadX = Math.cos(angle) * window.innerWidth * (isMobile ? 0.58 : 0.43) * reach + horizontalBias;
        const spreadY = Math.sin(angle) * window.innerHeight * (isMobile ? 0.54 : 0.4) * reach;

        return {
          born: now + Math.random() * 150 + index * 0.35,
          life: 850 + Math.random() * 650,
          x: originX + (Math.random() - 0.5) * 3,
          y: originY + (Math.random() - 0.5) * 2,
          targetX: originX + spreadX,
          targetY: originY + spreadY,
          radius: 0.35 + Math.random() * 0.75,
          depth: 0.65 + Math.random() * 1.2,
          drift: (Math.random() - 0.5) * 18,
          alpha: 0.12 + Math.random() * 0.24,
        };
      });

      if (!this.animationFrame) this.renderMist(now);
    }

    renderMist(time) {
      const context = this.context;
      if (!context) return;
      context.clearRect(0, 0, window.innerWidth, window.innerHeight);

      let active = false;
      this.particles.forEach((particle) => {
        const rawProgress = (time - particle.born) / particle.life;
        if (rawProgress < 0) {
          active = true;
          return;
        }
        if (rawProgress > 1) return;
        active = true;

        const progress = 1 - Math.pow(1 - rawProgress, 2.4);
        const depthScale = 1 + Math.pow(progress, 2.15) * 4.8 * particle.depth;
        const fade = Math.sin(Math.PI * rawProgress) * (1 - Math.max(0, rawProgress - 0.72) / 0.28);
        const x = particle.x + (particle.targetX - particle.x) * progress;
        const y = particle.y + (particle.targetY - particle.y) * progress + Math.sin(progress * Math.PI) * particle.drift;
        const radius = particle.radius * depthScale;
        const depthBlur = Math.max(0, progress - 0.26) * radius * 1.85;

        context.beginPath();
        context.ellipse(x, y, radius * 0.72, radius, 0, 0, Math.PI * 2);
        context.fillStyle = `rgba(226, 218, 203, ${particle.alpha * fade})`;
        context.shadowBlur = depthBlur;
        context.shadowColor = `rgba(218, 209, 193, ${particle.alpha * fade * 0.2})`;
        context.fill();
        context.shadowBlur = 0;

        if (progress > 0.62 && radius > 2.2) {
          context.beginPath();
          context.ellipse(x - radius * 0.18, y - radius * 0.22, radius * 0.13, radius * 0.18, 0, 0, Math.PI * 2);
          context.fillStyle = `rgba(255, 252, 245, ${particle.alpha * fade * 0.45})`;
          context.fill();
        }
      });

      if (active) {
        this.animationFrame = window.requestAnimationFrame((nextTime) => this.renderMist(nextTime));
      } else {
        this.animationFrame = null;
        context.clearRect(0, 0, window.innerWidth, window.innerHeight);
      }
    }

    syncTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
      if (this.reducedMotion.matches || !this.isVisible || document.hidden) return;
      this.timer = window.setInterval(() => this.spray(), this.interval);
    }

    destroy() {
      window.clearInterval(this.timer);
      window.clearTimeout(this.resetTimer);
      window.clearTimeout(this.emitTimer);
      window.cancelAnimationFrame(this.animationFrame);
      this.section.classList.remove('is-spraying');
      this.bottle?.classList.remove('is-spraying');
      this.observer?.disconnect();
      this.cap?.removeEventListener('click', this.onCapClick);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.reducedMotion.removeEventListener?.('change', this.onMotionChange);
      window.removeEventListener('resize', this.onResize);
    }
  }

  const instances = new WeakMap();

  const initialize = (scope = document) => {
    const sections = scope.matches?.(SELECTOR) ? [scope] : scope.querySelectorAll(SELECTOR);
    sections.forEach((section) => {
      instances.get(section)?.destroy();
      instances.set(section, new LegacyHeroSpray(section));
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector?.(SELECTOR) || (event.target.matches?.(SELECTOR) ? event.target : null);
    instances.get(section)?.destroy();
  });
})();
